// Web Speech API wrapper. Free, browser-native, multi-language.
// Browsers supported: Chrome/Edge/Safari (Android & iOS), Samsung Internet.
// Not supported: Firefox desktop (will return isSupported=false).

const LANG_MAP = {
    it: "it-IT",
    en: "en-US",
    ar: "ar-EG",
    bn: "bn-BD",
    sq: "sq-AL",
    ro: "ro-RO",
};

export function isSpeechSupported() {
    if (typeof window === "undefined") return false;
    return Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
}

export function getSpeechLangCode(uiLang) {
    return LANG_MAP[uiLang] || LANG_MAP.it;
}

/**
 * Start a speech recognition session.
 * @param {object} opts
 * @param {string} opts.lang - UI language code (it, en, ar, bn, sq, ro)
 * @param {(text: string, isFinal: boolean) => void} opts.onResult - called as user speaks
 * @param {(err: string) => void} opts.onError
 * @param {() => void} opts.onEnd
 * @param {boolean} [opts.continuous=false] - if true, mic stays on across pauses (auto-restarts)
 *   until the caller invokes stop(). Ideal for push-to-talk.
 * @returns {{ stop: () => void }} controller
 */
export function startListening({ lang = "it", onResult, onError, onEnd, continuous = false }) {
    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Ctor) {
        onError?.("not_supported");
        return { stop: () => {} };
    }
    const langCode = getSpeechLangCode(lang);

    let stopped = false;
    let accFinal = ""; // accumulated final transcripts (continuous mode)
    let rec = null;

    const buildRec = () => {
        const r = new Ctor();
        r.lang = langCode;
        // Single utterance for the classic HACCP flow; continuous for push-to-talk.
        r.continuous = continuous;
        r.interimResults = true;
        r.maxAlternatives = 1;

        r.onresult = (event) => {
            if (continuous) {
                // Accumulate final segments, show latest interim after them.
                let interim = "";
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const seg = event.results[i];
                    const piece = seg[0]?.transcript || "";
                    if (seg.isFinal) accFinal += (accFinal ? " " : "") + piece.trim();
                    else interim = piece;
                }
                const merged = (accFinal + (interim ? " " + interim : "")).trim();
                onResult?.(merged, false);
            } else {
                const last = event.results[event.results.length - 1];
                const piece = last?.[0]?.transcript || "";
                if (last?.isFinal) {
                    accFinal = piece.trim();
                    onResult?.(accFinal, false);
                } else {
                    onResult?.(piece.trim(), false);
                }
            }
        };

        r.onerror = (event) => {
            if (event.error === "aborted" || event.error === "no-speech") return;
            onError?.(event.error || "unknown");
        };

        r.onend = () => {
            if (stopped) return; // caller-initiated stop; handled below
            if (continuous) {
                // Auto-restart to keep mic "on" during natural pauses.
                try { r.start(); } catch { /* browsers may throw if already starting */ }
                return;
            }
            onResult?.(accFinal.trim(), true);
            onEnd?.();
        };
        return r;
    };

    rec = buildRec();
    try {
        rec.start();
    } catch (e) {
        onError?.(String(e?.message || e));
    }

    return {
        stop: () => {
            stopped = true;
            try { rec && rec.stop(); } catch {}
            onResult?.(accFinal.trim(), true);
            onEnd?.();
        },
    };
}
